import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Snackbar,
  Tabs,
  Tab
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { resetLogin, userDetailsFromToken, resetDemoQuestionMetadata, resetDemoQuestionIDs } from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import mixpanel from 'mixpanel-browser';
import { trackUserSignIn, trackAdConversion, trackReferralFunnel } from '../../utils/tracking';
import { clearConsumedReferralParam, getSafeNextPath } from '../../utils/attribution';
import {
  readQuizLinkToken,
  scrubQuizLinkNextFromLocation,
  toOpaqueQuizLinkPath,
} from '../../utils/url-secret-scrub';
import { getActiveReferralCode, clearReferralCode, getReferralCapturedAtForSignup } from '../../utils/referral';
import {
  appendDailyCampaignParams,
  buildOpaqueDailyLoginPath,
  getOpaqueDailyReturnPath,
  isDailyAbsiteEnabled,
} from '../../utils/daily-question-opaque';
import url from "../../constants/urlList";
import "./login.scss";
import PhoneAuthSection from './PhoneAuthSection';
import InstitutionalSignIn from '../institutional/InstitutionalSignIn';
import { getInstitutionalProfile, clearInstitutionalProfile } from '../institutional/institutional-profile-session';
import { isEmailCodeAnyAddressEnabled, isInstitutionalLoginEnabled } from '../institutional/institutional-flags';
import { isTokenUsable, isTokenPersistable, getPendingEmailVerify, PENDING_EMAIL_VERIFY_KEY } from '../../utils/session';
import { STALE_SESSION_KEYS } from '../../utils/stale-session';
import { isNativeCapacitorRuntime } from '../../services/nativeRuntime';
import { NAV_SURFACE } from "../../constants/navSurface";
import {
  runNativeGoogleOAuth,
  authorizeApple,
  appleSignIn,
  isAppleAuthCancel,
  isLikelyJwt,
} from '../../services/nativeAuthService';

import { DEFAULT_POST_LOGIN_PATH, POST_LOGIN_PATHS } from '../../constants/postLoginPaths';
import { clearGoogleSameTabSignIn, markGoogleSameTabSignIn } from '../../utils/google-same-tab-signin';
import {
  OAUTH_RETURN_PATH_AT_KEY,
  OAUTH_RETURN_PATH_KEY,
  clearOAuthReturnPath,
  takeOAuthReturnPath,
} from '../../utils/oauth-return-path';
import { clearGoogleConnectAttempt } from '../../utils/google-connect-handoff';
import { isFenced, leaveRecordingPage, markReplayIneligible } from '../../utils/replay-document';
import { isReplayFeatureAvailable } from '../../utils/replay-consent';
import { REPLAY_MASK } from "../../utils/replay-mask";
import { CREATE_QUIZ_PATH, isPracticeEntryPath } from "../../utils/absite-practice-intent";
import { FREE_DAILY_ABSITE_QUESTIONS } from "../../constants/freeAllowance";
import { buildLoginEscapeUrl, detectInAppBrowser, inAppBrowserLabel } from "../../utils/in-app-browser";
import InAppEscapePanel from "../exam-review-drawer/inapp-escape-panel";

const googleLogo = require("../../images/google-icon.png").default;
const NOTES_TO_QUIZ_PREFIX = '/notes-to-quiz/';

// Google OAuth now uses a same-tab redirect (see onGoogleSignUp / googleAuthRedirect):
// the browser leaves /login for the provider and returns to /socialAuth, so the
// intended post-login destination is stashed (utils/oauth-return-path, one
// round trip only) to be restored on return, and a pending flag tells /login to
// finish the sign-in when it re-mounts.
const OAUTH_PENDING_KEY = 'oauthPending';

// The Google return and the native, Apple and institutional sign-ins
// (finishNativeSession) drop the identity an earlier session left as they store
// a new token: every stale-session key except the token. Otherwise the new
// token is paired with another user's id, role or name (the signed-in redirect
// below would send them on as that user even when the user lookup fails), or
// with an abandoned phone sign-up's isProfileComplete 'false', which holds that
// redirect for good. The sign-in then writes what it knows itself, and the user
// lookup it starts writes userId and userdetails, and role when the user has
// one. (The phone sign-in in PhoneAuthSection writes its own keys.)
const forgetEarlierIdentity = () => {
  STALE_SESSION_KEYS
    .filter((key) => key !== 'token')
    .forEach((key) => window.localStorage.removeItem(key));
};

// When the user lookup one of those sign-ins started fails, the sign-in leaves
// no session at all, as the stale-session guard leaves a token the server
// rejects (failSignIn in LoginView). Apple (with an id)
// stored the id and isProfileComplete before the lookup, and the institutional
// sign-in also the role and name: the signed-in redirect below took them as a
// finished sign-in. A token without an id still sent / to Create Quiz, which
// has no id to work with in a new document.
const forgetUnfinishedSignIn = () => {
  STALE_SESSION_KEYS.forEach((key) => window.localStorage.removeItem(key));
};

// Whether /login should send the student straight on. The signed-in redirect
// on mount in LoginView says why each condition is there.
const hasSignedInSession = () => (
  !!window.localStorage.getItem("userId")
  && isTokenUsable(window.localStorage.getItem("token"))
  && window.localStorage.getItem("isProfileComplete") !== 'false'
  && !getPendingEmailVerify()
  && window.localStorage.getItem(OAUTH_PENDING_KEY) !== 'true'
);

// Whether /login opened only to pass through: a Google sign-in coming back to
// finish here, or a signed-in student it sends straight on. Read as the page
// opens, before the Google return below clears its mark.
const isPassingThrough = () => (
  hasSignedInSession()
  || window.localStorage.getItem(OAUTH_PENDING_KEY) === 'true'
);

const NOT_IN_APP = { inApp: false, app: null, os: 'other' };

// The phone section's first field to type in: MUI's Select keeps a hidden
// (aria-hidden) native input ahead of the phone number field.
const PHONE_SECTION_FIRST_FIELD = '.phone-auth-section input:not([type="hidden"]):not([aria-hidden="true"])';

// Read once as the page opens. The native app and an iOS home-screen web app
// are never in-app browsers (the detector's own guards).
const readInAppBrowser = () => {
  try {
    return detectInAppBrowser() || NOT_IN_APP;
  } catch (e) {
    return NOT_IN_APP;
  }
};

// The escape panel's opening line on this page, in the order of its buttons:
// the phone number here first, then Google in a real browser (Android opens
// this page in Chrome through an intent; iOS can only copy the link for the
// visitor to open in Safari).
const inAppEscapeLead = ({ app, os }) => {
  const where = inAppBrowserLabel(app);
  if (os === 'android') {
    return `Google sign-in doesn't work inside ${where}. Use your phone number here, or open this page in Chrome to use Google.`;
  }
  const browserName = os === 'ios' ? 'Safari' : 'your browser';
  return `Google sign-in doesn't work inside ${where}. Use your phone number here, or copy the link and open it in ${browserName} to use Google.`;
};

// The escape event's `platform` is the visit's acquisition label, as on the
// QLink drawer (?platform=, which /login keeps in its address), or 'unknown'.
const readPlatformLabel = () => {
  const value = new URLSearchParams(window.location.search).get('platform')?.trim().toLowerCase();
  return value && /^[a-z0-9_-]{1,50}$/.test(value) ? value : 'unknown';
};

// Default sign-in tab. Google stays the default (over phone) now that E3 replaced
// the popup with a same-tab redirect — the popup was the reason google-default was
// questioned for mobile-heavy referral traffic; a redirect is reliable on mobile
// Safari, so the higher-conversion Google path can lead. An explicit `?auth=phone`
// still opens phone-first. Inside an app's in-app browser (an ad opened in
// Reddit, Instagram…) Google refuses OAuth (`disallowed_useragent`), so Phone
// leads there unless the address asks for Google; the Google tab stays one tap
// away and offers the open-in-browser escape.
const getInitialAuthMethod = (inAppBrowser = NOT_IN_APP, passingThrough = false) => {
  if (isInstitutionalLoginEnabled() && getInstitutionalProfile()) return 'institutional';
  // A pending email-OTP proof lives in the PHONE flow — resume that surface on
  // reload so PhoneAuthSection can mount and re-open the verify screen
  // (round-2 finding 2: the marker held the redirect but the default Google
  // tab never mounted the component that consumes it).
  if (getPendingEmailVerify()) {
    return 'phone';
  }
  const auth = new URLSearchParams(window.location.search).get('auth');
  // Dark institutional (.edu/.org) tab: `?auth=institutional` opens it only
  // while the flag is on; flag off it falls through to the Google default.
  if (auth === 'institutional' && isInstitutionalLoginEnabled()) {
    return 'institutional';
  }
  // The same tab as "Email Code" for any address: `?auth=email` is its alias, only
  // while that flag is on.
  if (auth === 'email' && isEmailCodeAnyAddressEnabled()) {
    return 'institutional';
  }
  if (auth === 'phone' || auth === 'google') {
    return auth;
  }
  // Not while this page is only passing through (isPassingThrough): Phone
  // would mount the phone section for nothing on the way.
  return inAppBrowser.inApp && !passingThrough ? 'phone' : 'google';
};

const sanitizeOpaqueDailyLogin = () => {
  if (!isDailyAbsiteEnabled()) return undefined;
  const returnPath = getOpaqueDailyReturnPath(window.location.search);
  if (!returnPath) return undefined;
  const opaque = new URL(returnPath, window.location.origin).searchParams;
  const auth = new URLSearchParams(window.location.search).get('auth');
  // The campaign keys stay in the address, as store.js left them (ads
  // attribution contract); the return path above never carries them.
  const cleanLoginPath = appendDailyCampaignParams(buildOpaqueDailyLoginPath({
    auth,
    date: opaque.get('date'),
    revisionId: opaque.get('revisionId'),
    revisionHash: opaque.get('revisionHash'),
    touchId: opaque.get('touch'),
    grantId: opaque.get('grant'),
  }), window.location.search);
  try {
    window.history.replaceState(window.history.state, '', cleanLoginPath);
  } catch (_) {
    // The parsed in-memory return path remains raw-free even if history is unavailable.
  }
  return returnPath;
};

// The quiz-link return for this visit, read once as the page opens: always the
// token-free claim path (/notes-to-quiz/claim), or undefined when ?next= is not
// a quiz link. GTM and the Meta pixel are running on this page, GTM reports
// every history change and Mixpanel attaches the address to every event, so a
// claim token must never be navigated to, stored as the Google return path, or
// left in this page's own address. The app shell and the claim page already
// write ?next= token-free; a value that still carries a token (the shell could
// not store it) has it moved into the tab stash here and the address rewritten.
// `linkInThisTab` is false when the token-free path arrived where the link
// itself was never opened (another tab, browser or device): nothing here can
// claim it, so the page must not promise the quiz.
const readQuizLinkReturn = () => {
  const next = new URLSearchParams(window.location.search).get('next');
  if (!next || !next.startsWith(NOTES_TO_QUIZ_PREFIX) || next.startsWith('//')) {
    return { path: undefined, linkInThisTab: false };
  }
  scrubQuizLinkNextFromLocation();
  return { path: toOpaqueQuizLinkPath(next), linkInThisTab: readQuizLinkToken() !== null };
};

const LoginView = (props) => {
  const { onLoginRequest, onLoginSubmit } = props;
  const [userAcquisitionQuesId, setUserAcquisitionQuesId] = useState('');
  // false | true | 'institutional' — the last names the leg that failed so the
  // snackbar doesn't blame Google for an institutional sign-in.
  const [oauthError, setOAuthError] = useState(false);
  // Set while an institutional sign-in's token is resolving through
  // userDetailsFromToken; consumed by the effect below.
  const institutionalHandoffRef = useRef(false);
  // The sign-in this page started (the Google return, native Google, Apple,
  // institutional) whose user lookup is out: the lookup's request id, the
  // token the sign-in stored, and whether it is reported as Google's. null
  // when none is out; the phone sign-in does not set it. Only that lookup's
  // result finishes or fails the sign-in (see whoseLookup).
  const resolvingSignInRef = useRef(null);
  // Every lookup this page started, so a late result for a sign-in a later
  // one replaced is known as this page's and left alone.
  const startedLookupIdsRef = useRef(new Set());
  const [institutionalAttempt, setInstitutionalAttempt] = useState(0);
  const [institutionalProfilePending, setInstitutionalProfilePending] = useState(() => isInstitutionalLoginEnabled() && !!getInstitutionalProfile());
  const [inAppBrowser] = useState(readInAppBrowser);
  const [platformLabel] = useState(readPlatformLabel);
  // While the page only passes through, the Google tab keeps its button and
  // no escape panel: no "doesn't work" (and no "shown" event) while Google
  // finishes here or the student is sent on. A sign-in that fails ends it.
  const [passingThrough, setPassingThrough] = useState(isPassingThrough);
  const [authMethod, setAuthMethod] = useState(() => getInitialAuthMethod(inAppBrowser, passingThrough));
  const showInAppEscape = inAppBrowser.inApp && !passingThrough;
  const [opaqueDailyReturnPath] = useState(() => (
    isDailyAbsiteEnabled() ? sanitizeOpaqueDailyLogin() : undefined
  ));
  // Held from mount, like the Daily return above, so the return survives
  // anything that changes this page's address before the sign-in finishes.
  // (The phone section used to navigate first and empty this page's query; on
  // /login it no longer navigates at all.)
  const [quizLinkReturn] = useState(readQuizLinkReturn);
  const [invitePending, setInvitePending] = useState(false);
  const { handleSubmit } = useForm();
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const savedUserId = window.localStorage.getItem("userId");
    const userAcqQuesId = window.localStorage.getItem("userAcqQuesId");

    // Redirect unless the phone-OTP onboarding explicitly marked the profile
    // incomplete. The key is written only by the phone/native flows, so
    // email/OAuth web sessions never have it — treating absence as incomplete
    // forced every returning session back through sign-in on each cold start
    // (web root visit and every iOS app relaunch; iOS-branch #542).
    // The redirect additionally requires a USABLE token (decodes + unexpired):
    // a bare/expired session must render the sign-in form — redirecting on
    // userId alone composed with the notes-to-quiz claim page's token-only
    // clear into an infinite /login <-> claim-link loop (gate leg-2 B1).
    // A pending email-OTP proof (verified-email flag) also holds the redirect:
    // the marker means ownership of the captured email is not yet proven, and
    // PhoneAuthSection resumes the verify screen from it (GPT-5.6 finding 3).
    // A Google return still being completed holds it too: the completion below
    // navigates once the user lookup resolves. Sent on here first (a userId
    // left by an earlier session), the page unmounted before the lookup came
    // back, so the Google sign-in was never tracked and the earlier id stayed.
    if (hasSignedInSession()) {
      goToPostLoginPath();
    }
    // If profile  incomplete (isProfileComplete : 'false'), stay on login page
    // so PhoneAuthSection can show the profile completion form

    if (userAcqQuesId) {
      setUserAcquisitionQuesId(userAcqQuesId);
    }

    // A referral code is present only after visiting a friend's invite link;
    // acknowledging the invite meaningfully lifts referred-signup conversion.
    if (!savedUserId && getActiveReferralCode(window.localStorage)) {
      setInvitePending(true);
      trackReferralFunnel('referral_invite_landing_viewed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Back can show an earlier /login document from the back/forward cache: its
  // scripts resume as they were and nothing remounts, so the check above never
  // re-runs, and a student who has signed in since (in another tab, or through
  // the Google round trip this document started) saw its sign-in form. Re-run
  // the same check when such a document is shown.
  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) return;
      // Back from Google restores the /login that left for it with its
      // same-tab mark still here; that round trip is over, so a /socialAuth
      // link opened in this tab next must not find the mark.
      clearGoogleSameTabSignIn();
      if (hasSignedInSession()) {
        goToPostLoginPath();
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finish a same-tab Google OAuth round-trip. googleAuthRedirect (/socialAuth)
  // stashed the token + a pending flag and bounced back to /login; complete the
  // sign-in through the same resolution path the popup flow used (fetch the user
  // from the token, then navigate on success / show the error snackbar on
  // failure) so success, tracking, and error handling stay in one place.
  useEffect(() => {
    const pending = window.localStorage.getItem(OAUTH_PENDING_KEY) === 'true';
    const token = window.localStorage.getItem('token');
    if (pending && token) {
      window.localStorage.removeItem(OAUTH_PENDING_KEY);
      forgetEarlierIdentity();
      clearReferralCode(window.localStorage);
      clearConsumedReferralParam();
      lookUpSignedInUser(token, { google: true, door: 'google' });
    } else {
      if (pending) {
        // Bounced back without a token — treat as a failed sign-in, don't loop.
        window.localStorage.removeItem(OAUTH_PENDING_KEY);
        setOAuthError(true);
      }
      // Not completing an OAuth return on this mount (gate B2): any stashed
      // return path belongs to an attempt that never came back (e.g. the user
      // backed out at Google). Sweep it so only the round-trip it was written
      // for can consume it — never a later phone/other login.
      clearOAuthReturnPath();
      // Same for this tab's same-tab sign-in mark (best effort: a
      // back/forward-cache restore does not remount; the popup callers clear
      // it before window.open, which copies sessionStorage).
      clearGoogleSameTabSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A sign-in failed (the Google return included): the visitor stays on this
  // page, and inside an in-app browser its Google tab offers the escape again.
  useEffect(() => {
    if (oauthError) setPassingThrough(false);
  }, [oauthError]);

  // The escape panel's "Use phone number" unmounts the button that had focus.
  // Once the Phone tab is up, focus moves to the first field to type in (the
  // phone number; not the country picker's hidden input), or else that tab.
  const phoneTabRef = useRef(null);
  const focusPhoneOnSwitchRef = useRef(false);
  const usePhoneFromEscape = () => {
    focusPhoneOnSwitchRef.current = true;
    setAuthMethod('phone');
  };
  useEffect(() => {
    if (authMethod !== 'phone' || !focusPhoneOnSwitchRef.current) return;
    focusPhoneOnSwitchRef.current = false;
    const firstField = document.querySelector(PHONE_SECTION_FIRST_FIELD);
    (firstField || phoneTabRef.current)?.focus();
  }, [authMethod]);

  // The phone code screen's "Use Google instead" unmounts the button that had
  // focus. Once the Google tab is up, focus moves to its Google button, or,
  // where the tab shows the in-app escape instead, to the tab itself.
  const googleTabRef = useRef(null);
  const focusGoogleOnSwitchRef = useRef(false);
  useEffect(() => {
    if (authMethod !== 'google' || !focusGoogleOnSwitchRef.current) return;
    focusGoogleOnSwitchRef.current = false;
    (document.getElementById('google_login_btn') || googleTabRef.current)?.focus();
  }, [authMethod]);

  useEffect(() => {
    if (getInstitutionalProfile()) return;
    if (onLoginRequest.errorMsg) {
      setTimeout(() => {
        dispatch(resetLogin());
      }, 2000);
    } else if (onLoginRequest.user) {
      mixpanel.track('Sign In', {
        $name: onLoginRequest.user?.displayName,
        $user_id: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email,
        role: onLoginRequest.user.primaryRole
      });
      mixpanel.identify(onLoginRequest.user?.displayName);
      // Set user properties
      mixpanel.people.set({
        $email: onLoginRequest.user?.email,
        $name: onLoginRequest.user?.displayName,
        $role: onLoginRequest.user.primaryRole
      });
      // navSurface: a session-start marker, NOT a nav tap. This shares an event
      // name with the Practice tab's destination, so leaving it unstamped would
      // put login volume — DAU-proportional and flat across the nav flip — into
      // the same (not set) bucket the post-flip split has to read.
      mixpanel.track("Menu-create-quiz", {
        $name: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email,
        role: onLoginRequest.user.primaryRole,
        navSurface: NAV_SURFACE.AUTH_REDIRECT
      });

      // GTM and Facebook Pixel tracking for email/password login
      trackUserSignIn(onLoginRequest.user, 'email');
      trackAdConversion(onLoginRequest.user, 'email_login');

      // No session-replay call here. It used to be Hotjar.init directly, then
      // syncSessionReplay(window.location.pathname) — which DROPS the query
      // string, so signing in at /login?next=%2Fnotes-to-quiz%2F<TOKEN>
      // un-suppressed the page and started a recorder on a URL carrying the
      // token. <SessionReplayGate> already runs on every navigation and is
      // idempotent, so this call was redundant on the happy path and harmful on
      // that one.

      const userData = {
        name: onLoginRequest.user?.displayName,
        email: onLoginRequest.user?.email
      };
      window.localStorage.setItem("userdetails", JSON.stringify(userData));

      window.localStorage.setItem("token", onLoginRequest.user.accessToken);
      window.localStorage.setItem("userId", onLoginRequest.user.id);
      window.localStorage.setItem("role", onLoginRequest.user.primaryRole);

      if (userAcquisitionQuesId) {
        // goToDemoExam();
        dispatch(resetDemoQuestionMetadata());
        dispatch(resetDemoQuestionIDs()); 
        window.localStorage.removeItem("userAcqQuesId");
        window.localStorage.removeItem("userAcquisitiontoken");
      } 
      
      goToPostLoginPath();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, onLoginRequest]);

  const userDetailsFromTokenRes = useSelector((state) => {
    return state.userDetailsFromToken?.userDetails;
  });
  // A failed lookup leaves no user in the store, only this message.
  const userDetailsFromTokenError = useSelector((state) => state.userDetailsFromToken?.errorMsg);
  // Which lookup the store's result answers (actions/index.js).
  const userDetailsFromTokenRequestId = useSelector((state) => state.userDetailsFromToken?.requestId);

  // Look up the user a sign-in this page started has just stored a token for.
  // `door` names the sign-in for the account-created event.
  const lookUpSignedInUser = (token, { google = false, door } = {}) => {
    const lookup = userDetailsFromToken({ door });
    startedLookupIdsRef.current.add(lookup.requestId);
    resolvingSignInRef.current = { requestId: lookup.requestId, token, google };
    dispatch(lookup);
  };

  // Whose sign-in the store's result answers:
  // - 'mine': the sign-in this page has out, whose token is still the stored one.
  // - 'cleared': that sign-in's, but no token is stored any more. On a 401
  //   the stale-session guard clears it just before the failure arrives (the
  //   lookup is its own probe route); a failed phone lookup clears its own.
  // - 'replaced': that sign-in's, but another sign-in (the phone section, or
  //   another tab) has stored its own token since, and its own lookup decides.
  // - 'earlier': a sign-in this page started that a later one replaced.
  // - 'other': a lookup this page did not start: the phone section's, or one
  //   from before this page opened (a user the store still holds when /login
  //   is opened again in the same visit, or after the stale-session guard
  //   cleared the session).
  // A result that does not say which request it answers is taken as the
  // sign-in's this page has out, as before.
  const whoseLookup = (requestId) => {
    const signIn = resolvingSignInRef.current;
    if (signIn && (requestId === undefined || requestId === signIn.requestId)) {
      const storedToken = window.localStorage.getItem('token');
      if (storedToken === signIn.token) return 'mine';
      return storedToken ? 'replaced' : 'cleared';
    }
    return startedLookupIdsRef.current.has(requestId) ? 'earlier' : 'other';
  };

  // The sign-in this page has out is over. An institutional form still
  // saying "signing you in" is remounted, so it can be used again.
  const settleSignIn = () => {
    const institutional = institutionalHandoffRef.current;
    resolvingSignInRef.current = null;
    institutionalHandoffRef.current = false;
    if (institutional) {
      setInstitutionalAttempt((n) => n + 1);
    }
    return institutional;
  };

  // Store the id of the student a sign-in on this page just resolved, as the
  // email sign-in does from its response. Google returns only a token and
  // nothing else stored the id until Create Quiz or an exam page did, so the
  // signed-in redirect above failed for a Google student who opened /login
  // from anywhere else. The role and name go with it, as on Create Quiz.
  const storeUserThisPageResolved = (user) => {
    if (!user?.id) return;
    window.localStorage.setItem("userId", user.id);
    if (user.primaryRole) {
      window.localStorage.setItem("role", user.primaryRole);
    }
    window.localStorage.setItem("userdetails", JSON.stringify({
      name: user.displayName,
      email: user.email
    }));
  };

  // A user resolved. When the lookup was this page's sign-in's, that sign-in
  // is finished: its user is stored and, for Google, reported. Any resolved
  // user sends the student on, as before (a phone sign-in, or a user the
  // store still holds when /login is opened again in the same visit). Only
  // the demo-question hand-off below stores a user no sign-in on this page
  // asked for, as before.
  const finishSignIn = (user, mine) => {
    const signIn = mine ? resolvingSignInRef.current : null;
    // A COMPLETED sign-in supersedes any pending phone-flow email proof:
    // the phone path clears the marker on OTP success BEFORE dispatching
    // this fetch, so a marker still present here belongs to an abandoned
    // flow another method just replaced — clear it so it cannot strand or
    // misroute the fresh session (round-2 finding 2).
    window.localStorage.removeItem(PENDING_EMAIL_VERIFY_KEY);
    institutionalHandoffRef.current = false;
    if (signIn) {
      resolvingSignInRef.current = null;
      storeUserThisPageResolved(user);
    }
    // GTM and Facebook Pixel tracking for Google OAuth login with actual role
    if (signIn?.google) {
      const oauthUserData = {
        id: user?.id,
        email: user?.email,
        displayName: user?.displayName,
        primaryRole: user?.primaryRole // Use actual role from server
      };
      trackUserSignIn(oauthUserData, 'google_oauth');
      trackAdConversion(oauthUserData, 'google_oauth');
    }

    if (userAcquisitionQuesId && user?.id) {
      window.localStorage.setItem("userId", user.id);
      window.localStorage.setItem("role", user.primaryRole);
      window.localStorage.removeItem("userAcqQuesId");
      window.localStorage.removeItem("userAcquisitiontoken");
    }
    goToPostLoginPath();
  };

  // The lookup for the sign-in this page has out failed. Was a native
  // alert('User not registered') — jarring, and it dead-ended exactly the new
  // referral users this flow is optimizing for. Surface a non-blocking
  // snackbar with a way forward (phone sign-in) instead. The sign-in did not
  // finish, so it leaves no session.
  const failSignIn = () => {
    const institutional = settleSignIn();
    forgetUnfinishedSignIn();
    setOAuthError(institutional ? 'institutional' : true);
  };

  useEffect(() => {
    if (getInstitutionalProfile()) return;
    const resolved = userDetailsFromTokenRes?.status === 200 || !!userDetailsFromTokenRes?.id;
    if (!resolved && !userDetailsFromTokenRes && !userDetailsFromTokenError) return;
    const whose = whoseLookup(userDetailsFromTokenRequestId);
    // A failure of a lookup this page did not start ('other') is not its to
    // report: the phone section reports its own.
    if (whose === 'earlier') return;
    if (whose === 'replaced' || (whose === 'cleared' && resolved)) {
      settleSignIn();
    } else if (resolved) {
      finishSignIn(userDetailsFromTokenRes, whose === 'mine');
    } else if (whose === 'mine' || whose === 'cleared') {
      failSignIn();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFromTokenRes, userDetailsFromTokenError, userDetailsFromTokenRequestId]);

  const getNotesToQuizNextPath = () => quizLinkReturn.path;

  const getPostLoginPath = () => (
    opaqueDailyReturnPath || getNotesToQuizNextPath() || getSafeNextPath(window.location.search, {
      allowedPathnames: isDailyAbsiteEnabled()
        ? POST_LOGIN_PATHS
        : POST_LOGIN_PATHS.filter((path) => path !== '/daily-absite-question'),
    }) || DEFAULT_POST_LOGIN_PATH
  );

  // One post-login navigation per mount. The first navigation leaves /login, so
  // this page unmounts; a second call in the same update (the signed-in
  // redirect on mount and the resolved-user effect both fire when the store
  // still holds the user, e.g. /login opened again in the same visit) would
  // re-read the moved address and, for a ?next= return, replace the right page
  // with Create Quiz.
  const postLoginNavigatedRef = useRef(false);

  // Replace, never push, for every sign-in method and for the signed-in
  // redirect on mount: once this runs the student is signed in, and /login
  // would only send them on again, so its entry is never somewhere to go back
  // to. Pushed, Back from the destination re-entered /login and was sent
  // forward again (a trap), and after Google the re-entered bare /login sent
  // them to Create Quiz. (GA4, the Meta pixel and UET count a replaceState page
  // change like a pushState; checked against their scripts 2026-10-14.)
  const goToPostLoginPath = () => {
    if (postLoginNavigatedRef.current) return;
    postLoginNavigatedRef.current = true;
    // A same-tab OAuth round-trip returns to a bare /login (its original ?next=
    // is gone), so prefer the destination stashed before the redirect. The value
    // was itself produced by getPostLoginPath(), so it is already allowlist-safe;
    // takeOAuthReturnPath additionally enforces the one-round-trip TTL (gate B2)
    // and the mount sweep above removed any stash from an aborted attempt. For
    // every other flow the key is absent and behavior is unchanged.
    const stashed = takeOAuthReturnPath();
    if (stashed) {
      // A Google round trip that left before the return path was written
      // token-free can still come back holding a quiz-link claim token.
      history.replace(toOpaqueQuizLinkPath(stashed));
      return;
    }
    history.replace(getPostLoginPath());
  };


  const signIn = (data) => {
    onLoginSubmit(data);
  };

  const onGoogleSignUp = () => {
    // E3: same-tab redirect instead of window.open. The popup was blocked or
    // silently failed on mobile Safari (and mobile Chrome) — the primary,
    // mobile-heavy referral-conversion path landed on a blank state. The
    // referral `ref` is forwarded on the authorization URL EXACTLY as the popup
    // did (backend consumes it server-side during OAuth); nothing needs to
    // survive the return trip except the token, which the backend redirects
    // back to /socialAuth?token=… — the same redirect URI the popup used, so no
    // backend change is required. googleAuthRedirect handles that landing.
    const referralCode = opaqueDailyReturnPath
      ? undefined
      : getActiveReferralCode(window.localStorage);
    const referralCapturedAt = referralCode
      ? getReferralCapturedAtForSignup(window.localStorage)
      : null;
    const oauthUrl = referralCode
      ? `${url.domain}/qlearn/oauth2/authorization/google?ref=${encodeURIComponent(referralCode)}${
        referralCapturedAt ? `&refAt=${encodeURIComponent(referralCapturedAt)}` : ''}`
      : `${url.domain}/qlearn/oauth2/authorization/google`;
    // Stash the intended post-login destination (the /login ?next= is lost across
    // the same-tab redirect) so goToPostLoginPath can restore it on return. The
    // timestamp scopes the stash to this round-trip (gate B2): consumption
    // ignores it past a short TTL, and any /login mount that isn't completing an
    // OAuth return sweeps it.
    try {
      window.localStorage.setItem(OAUTH_RETURN_PATH_KEY, getPostLoginPath());
      window.localStorage.setItem(OAUTH_RETURN_PATH_AT_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable → return path falls back to the default; the
      // sign-in itself still proceeds.
    }
    // /socialAuth lets only the tab that started a same-tab sign-in replace a
    // live session with the result (a Connect Google popup that lost its
    // opener lands there too). A Connect Google mark would make it refuse
    // this sign-in, and this tab is signing in.
    clearGoogleConnectAttempt();
    markGoogleSameTabSignIn();
    window.location.assign(oauthUrl);
  };

  // ── Dark native trio (Step 2) ───────────────────────────────────────────────
  // A strict native sign-in card (Apple / Google / Phone, no email surface),
  // reachable ONLY inside the Capacitor shell AND with the exact build flag set.
  // On web `isNativeCapacitorRuntime()` is false, so the whole branch is inert
  // and the existing web login renders byte/DOM-identically (flag-off parity).
  const nativeTrioEnabled =
    isNativeCapacitorRuntime() &&
    process.env.REACT_APP_QLEARN_IOS_SIGNIN_TRIO === 'true';

  // 'options' = the three-button card; 'phone' = PhoneAuthSection + a back control.
  // A pending email-OTP proof resumes the phone surface here too (round-2
  // finding 2) — the marker's verify screen lives inside PhoneAuthSection.
  const [nativeView, setNativeView] = useState(
    () => (getPendingEmailVerify() ? 'phone' : 'options')
  );
  // The active native-Google controller, retained so it is disposed exactly once
  // on unmount and before any retry (the service owns a single live attempt).
  const googleControllerRef = useRef(null);
  // Monotonic attempt token. `runNativeGoogleOAuth()` resolves its controller only
  // AFTER serialized admission, so a retry/unmount/back can occur while admission is
  // still pending. Bumping this token supersedes a pending attempt: when its
  // controller finally arrives, the attempt disposes it and never persists a result.
  const googleAttemptRef = useRef(0);
  // Guards against disposing the same controller twice (idempotent teardown).
  const disposedControllersRef = useRef(new WeakSet());
  // Guards async completions from setting state after the surface unmounts.
  const isMountedRef = useRef(true);

  // Dispose a specific controller exactly once, whichever path reaches it first
  // (attempt finally, retry supersede, back, or unmount).
  const safeDisposeController = (controller) => {
    if (!controller || typeof controller.dispose !== 'function') return;
    if (disposedControllersRef.current.has(controller)) return;
    disposedControllersRef.current.add(controller);
    try {
      controller.dispose();
    } catch (e) {
      // dispose is best-effort teardown; a throw here must not surface to the UI.
    }
  };

  const disposeGoogleController = () => {
    const controller = googleControllerRef.current;
    googleControllerRef.current = null;
    safeDisposeController(controller);
  };

  // Supersede any pending/settled native-Google attempt: bump the token so a
  // still-pending admission disposes its eventual controller instead of persisting,
  // and dispose a controller already retained in the ref.
  const supersedeGoogleAttempt = () => {
    googleAttemptRef.current += 1;
    disposeGoogleController();
  };

  // Dispose any in-flight native-Google attempt when the login surface unmounts.
  useEffect(() => () => {
    isMountedRef.current = false;
    supersedeGoogleAttempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Complete a native sign-in through the SAME resolution path the web flows use:
  // persist the session token and hand off to userDetailsFromToken (the effect
  // above navigates on success). No linking/identity logic lives here — the
  // backend owns link-by-verified-email-or-create.
  const finishNativeSession = ({ token, id, isProfileComplete, google = false, door }) => {
    if (token) {
      window.localStorage.setItem('token', token);
      forgetEarlierIdentity();
    }
    if (id) window.localStorage.setItem('userId', id);
    if (typeof isProfileComplete !== 'undefined') {
      window.localStorage.setItem('isProfileComplete', isProfileComplete ? 'true' : 'false');
    }
    clearInstitutionalProfile();
    clearReferralCode(window.localStorage);
    clearConsumedReferralParam();
    lookUpSignedInUser(token ?? null, { google, door });
  };

  // Institutional (.edu/.org) email OTP sign-in (dark: REACT_APP_INSTITUTIONAL_LOGIN_ENABLED).
  // InstitutionalSignIn has already gated the token on isTokenPersistable; the
  // session then rides the SAME finishNativeSession path as Apple/native Google
  // (token/userId/isProfileComplete, referral clear, userDetailsFromToken ->
  // goToPostLoginPath), plus the userdetails/role keys the email-login path
  // writes from its JwtResponse. isProfileComplete is forwarded only when the
  // server sent a real boolean — a null would otherwise persist 'false' and
  // hold every later session restore on /login.
  // InstitutionalSignIn passes { door: 'email_code' } only for a non-school address
  // while the any-address flag is on; every other sign-in keeps 'institutional_email'.
  const finishInstitutionalSession = (user, { door } = {}) => {
    const loginMethod = door === 'email_code' ? 'email_code' : 'institutional_email';
    trackUserSignIn(user, loginMethod);
    trackAdConversion(user, loginMethod);
    institutionalHandoffRef.current = true;
    finishNativeSession({
      token: user.accessToken,
      id: user.id,
      ...(typeof user.isProfileComplete === 'boolean'
        ? { isProfileComplete: user.isProfileComplete }
        : {}),
      door: loginMethod === 'email_code' ? 'email_code' : 'institutional',
    });
    // After finishNativeSession, which drops the earlier session's identity.
    // The user lookup it started resolves later (a network round trip).
    window.localStorage.setItem("userdetails", JSON.stringify({
      name: user.displayName,
      email: user.email
    }));
    if (user.primaryRole) {
      window.localStorage.setItem("role", user.primaryRole);
    }
  };

  // Native Google (iOS ruling C2(b)): the service runs the flow in ASWebAuthenticationSession
  // through the QlearnAuthSession plugin and fails closed without it. `runNativeGoogleOAuth`
  // returns a Promise that resolves the controller AFTER serialized admission — it is
  // NOT the controller itself. We await it while holding this attempt's token: if a
  // retry, unmount, or back-to-options supersedes the attempt during admission, the
  // eventual controller is disposed and its result is never persisted. The service
  // validates state (CSRF) + JWT-shape internally; persistence is additionally
  // gated on the shared utils/session predicate before the token is stored.
  const onNativeGoogleSignIn = async () => {
    // Dispose a settled prior controller, then claim a fresh attempt token.
    disposeGoogleController();
    const attempt = (googleAttemptRef.current += 1);
    const isCurrent = () => isMountedRef.current && googleAttemptRef.current === attempt;
    setOAuthError(false);
    let controller;
    try {
      const referralCode = getActiveReferralCode(window.localStorage);
      const referralCapturedAt = referralCode
        ? getReferralCapturedAtForSignup(window.localStorage)
        : null;
      // Await the deferred admission to obtain the controller.
      controller = await runNativeGoogleOAuth({
        domain: url.domain,
        referralCode,
        referralCapturedAt,
      });
      // Superseded (retry/unmount/back) while admission was pending: own the eventual
      // controller only to tear it down; never persist its result.
      if (!isCurrent()) {
        safeDisposeController(controller);
        return;
      }
      googleControllerRef.current = controller;
      const outcome = await controller.result;
      if (!isCurrent()) return;
      // Persistence is gated by the ONE shared predicate (utils/session:
      // typed NumericDate exp, in the future) — never a parallel local check.
      if (outcome && outcome.ok && isTokenPersistable(outcome.token)) {
        finishNativeSession({ token: outcome.token, google: true, door: 'google' });
      } else if (!(outcome && outcome.cancelled)) {
        // A sheet the user closed is silent, like an Apple cancel; anything else is an error.
        setOAuthError(true);
      }
    } catch (e) {
      if (isCurrent()) setOAuthError(true);
    } finally {
      // Dispose this attempt's controller exactly once (idempotent).
      safeDisposeController(controller);
    }
  };

  // Native Apple: consume the reviewed Step-1 seam (plugin/token/cancel logic all
  // live in the service — never reimplemented here). A user-cancelled sheet is
  // silent; every other failure surfaces the shared error snackbar.
  const onAppleSignIn = async () => {
    setOAuthError(false);
    try {
      const response = await authorizeApple();
      if (!isMountedRef.current) return;
      // Guard the Apple identity token BEFORE exchange (Step-1 isLikelyJwt seam;
      // no reimplemented parsing). A malformed token never reaches the backend.
      if (!response || !isLikelyJwt(response.identityToken)) {
        setOAuthError(true);
        return;
      }
      const fullName = [response.givenName, response.familyName].filter(Boolean).join(' ');
      // The one-time authorizationCode lets the backend keep a revocable Apple token, so account
      // deletion can revoke this sign-in at Apple (iOS ruling B1 §5).
      const session = await appleSignIn({
        identityToken: response.identityToken,
        ...(fullName ? { fullName } : {}),
        ...(typeof response.authorizationCode === 'string' && response.authorizationCode
          ? { authorizationCode: response.authorizationCode } : {}),
      });
      if (!isMountedRef.current) return;
      // Guard the returned QLearn access token BEFORE persistence — the ONE
      // shared predicate (utils/session), same contract as the Google leg.
      if (!session || session.errStatus || !isTokenPersistable(session.accessToken)) {
        setOAuthError(true);
        return;
      }
      finishNativeSession({
        token: session.accessToken,
        id: session.id,
        isProfileComplete: session.isProfileComplete,
        door: 'apple',
      });
    } catch (err) {
      if (isAppleAuthCancel(err)) return; // user cancelled — silent, per spec
      if (isMountedRef.current) setOAuthError(true);
    }
  };

  // A page that records (Hotjar, M3) must not show the institutional or
  // email-code sign-in: it echoes the typed address, stores the session token
  // and shows its name step on this same document. On a recording page, leave
  // by a full page load for ?auth=institutional instead; that address never
  // records, and the tab opens there exactly as it does today. On a page that
  // is not recording (turned off, GPC, or not yet), the tab opens in place and
  // the page is marked so it can never start recording (a later "Turn on"
  // applies from the next load). Every other tab change stays in place.
  const handleAuthMethodChange = (_, newValue) => {
    if (newValue === 'institutional') {
      if (isFenced()) {
        const params = new URLSearchParams(window.location.search);
        params.set('auth', 'institutional');
        leaveRecordingPage(`/login?${params.toString()}`);
        return;
      }
      markReplayIneligible();
    }
    setAuthMethod(newValue);
  };

  // "Use Google instead" on the phone code screen: the same switch as the
  // Google tab, so inside an in-app browser it opens the escape panel, not a
  // Google button that would fail there.
  const switchToGoogleFromPhone = () => {
    focusGoogleOnSwitchRef.current = true;
    handleAuthMethodChange(null, 'google');
  };
  // Its words: inside an in-app browser that Google tab is the escape panel,
  // so the button names where Google will work (the panel's own browsers).
  let phoneGoogleLabel = 'Use Google instead';
  if (showInAppEscape) {
    if (inAppBrowser.os === 'ios') phoneGoogleLabel = 'Use Google in Safari';
    else if (inAppBrowser.os === 'android') phoneGoogleLabel = 'Use Google in Chrome';
    else phoneGoogleLabel = 'Use Google in your browser';
  }

  const goBackToNativeOptions = () => {
    // Leaving the options view supersedes any pending Google admission so a late
    // controller cannot complete a sign-in the user has navigated away from.
    supersedeGoogleAttempt();
    setNativeView('options');
  };

  // Every real transition INTO native Phone auth (the direct Phone choice AND the
  // snackbar fallback) must supersede any pending/settled Google attempt BEFORE the
  // view changes: a late deferred Google controller/result is then disposed exactly
  // once and can never persist a token, dispatch session resolution, or navigate
  // over PhoneAuthSection.
  const enterNativePhoneView = () => {
    supersedeGoogleAttempt();
    setNativeView('phone');
  };

  // The allowed ?next= the line over the sign-in options speaks to. The ABSITE
  // practice entry (/create-quiz?practice=absite, which store.js sends a
  // signed-out visitor here from) gets its own line: that visitor mostly comes
  // from www's ABSITE pages or a practice email, often for the first time, so
  // "continue where you left off" would be wrong. It does not repeat "sign in
  // or create an account" (the heading below says it), and the free allowance
  // is said as what a free account includes, so a paid student whose session
  // expired does not read it as their plan. A referral invite already offers
  // 7 days of Pro above it, so then the line names the practice only.
  const continueNextPath = getSafeNextPath(window.location.search, {
    allowedPathnames: POST_LOGIN_PATHS,
  });
  const practiceEntryNote = invitePending
    ? 'Start your ABSITE practice.'
    : `Start your ABSITE practice. A free account includes ${FREE_DAILY_ABSITE_QUESTIONS} ABSITE questions a day.`;
  // Any other Create Quiz return gets no line, as on a bare /login. store.js
  // sends every signed-out visitor on /create-quiz here with it, many on a
  // first visit from an email or an ad, so "continue where you left off"
  // would be wrong; and Create Quiz is where a sign-in lands anyway.
  const continueNote = (() => {
    if (!continueNextPath) return null;
    if (isPracticeEntryPath(continueNextPath)) return practiceEntryNote;
    // getSafeNextPath returned it as an allowed pathname plus query/hash.
    if (continueNextPath.split(/[?#]/, 1)[0] === CREATE_QUIZ_PATH) return null;
    return 'Sign in to continue where you left off.';
  })();

  return (
    <>
      <Snackbar open={!!oauthError} onClose={() => setOAuthError(false)} autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <>
          {/* data-hj-suppress (review B2): /login is recorded, and error
              text must never echo what was typed. */}
          <Box className="success-warn" data-testid="oauth-error" {...REPLAY_MASK}>
            {nativeTrioEnabled ? (
              <>
                We couldn&apos;t sign you in. Please try again, or continue with
                phone.
                <Button
                  size="small"
                  className="oauth-error-action"
                  onClick={() => { enterNativePhoneView(); setOAuthError(false); }}
                >
                  Continue with phone
                </Button>
              </>
            ) : (
              <>
                {oauthError === 'institutional' && isEmailCodeAnyAddressEnabled() ? (
                  <>
                    We couldn&apos;t finish signing you in with your email code.
                    Please try again, or use phone sign-in instead.
                  </>
                ) : oauthError === 'institutional' ? (
                  <>
                    We couldn&apos;t finish signing you in with your institutional
                    email. Please try again, or use phone sign-in instead.
                  </>
                ) : (
                  <>
                    We couldn&apos;t sign you in with Google. Please try again, or use
                    phone sign-in instead.
                  </>
                )}
                <Button
                  size="small"
                  className="oauth-error-action"
                  onClick={() => { setAuthMethod('phone'); setOAuthError(false); }}
                >
                  Use phone sign-in
                </Button>
              </>
            )}
          </Box>
        </>
      </Snackbar>
      <Box className="login-body">
        <Box component="div" className="app-name">
          QLearn
        </Box>
        <Box component="div" className="app-tagline">
          Board-exam question practice by Medlearnity
        </Box>

        <Box component="div" className="login-container">
          {invitePending && (
            <Box
              data-testid="referral-invite-banner"
              style={{
                background: '#e8f5e9', color: '#1b5e20', borderRadius: 4,
                padding: '8px 12px', marginBottom: 16, fontSize: 14, textAlign: 'center'
              }}
            >
              🎁 A friend invited you to QLearn — sign up below and get 7 days of Pro free.
            </Box>
          )}
          {/* Server text can quote the address or number typed (review B2). */}
          <Box component="div" className="error-container" {...REPLAY_MASK}>
            {onLoginRequest.errorMsg}
          </Box>

          {getNotesToQuizNextPath() ? (
            <Box component="div" className="auth-continue-note">
              {quizLinkReturn.linkInThisTab ? (
                <>
                  Sign in to open your session-review quiz. Use the email address
                  that received the quiz link.
                </>
              ) : (
                <>
                  To open your session-review quiz, open the quiz link in your
                  email again.
                </>
              )}
            </Box>
          ) : continueNote && (
            <Box component="div" className="auth-continue-note">
              {continueNote}
            </Box>
          )}

          {nativeTrioEnabled ? (
            <Box className="native-auth-card" data-testid="native-auth-card">
              {nativeView === 'options' ? (
                <Box className="native-auth-options">
                  {/* Guideline 4.8: the privacy-preserving option is offered first,
                      with equal-or-greater prominence, whenever Google is shown. */}
                  <Button
                    variant="contained"
                    className="native-auth-btn native-apple-btn"
                    data-testid="native-apple-btn"
                    onClick={onAppleSignIn}
                  >
                    Sign in with Apple
                  </Button>
                  <Button
                    variant="outlined"
                    className="native-auth-btn native-google-btn"
                    data-testid="native-google-btn"
                    onClick={onNativeGoogleSignIn}
                  >
                    <img alt="google" src={googleLogo} width='20' height='20' className="logo" />
                    {' '}Sign in with Google
                  </Button>
                  <Button
                    variant="outlined"
                    className="native-auth-btn native-phone-btn"
                    data-testid="native-phone-btn"
                    onClick={enterNativePhoneView}
                  >
                    Continue with Phone
                  </Button>
                </Box>
              ) : (
                <Box className="native-auth-phone">
                  <Button
                    className="native-back-btn"
                    data-testid="native-back-to-options"
                    onClick={goBackToNativeOptions}
                  >
                    ← Back to options
                  </Button>
                  <PhoneAuthSection navigateOnSignIn={false} />
                </Box>
              )}
            </Box>
          ) : (
          <>
          {!isNativeCapacitorRuntime() && (
            <>
              <Box component="h1" className="auth-entry-heading">
                Create an account or sign in
              </Box>
              <Box component="p" className="auth-entry-description">
                {isEmailCodeAnyAddressEnabled() ? (
                  <>
                    New to QLearn? Use Google, your phone number or a code sent to your
                    email to create your account and start practicing.
                  </>
                ) : isInstitutionalLoginEnabled() ? (
                  <>
                    New to QLearn? Use Google, your phone number or your
                    institutional (.edu or .org) email to create your account
                    and start practicing.
                  </>
                ) : (
                  <>
                    New to QLearn? Use Google or your phone number to create your
                    account and start practicing.
                  </>
                )}
              </Box>
            </>
          )}
          <Tabs
            value={authMethod}
            onChange={handleAuthMethodChange}
            centered={!isInstitutionalLoginEnabled()}
            variant={isInstitutionalLoginEnabled() ? 'fullWidth' : 'standard'}
            indicatorColor="primary"
            textColor="primary"
            style={{ marginBottom: '20px' }}
          >
            <Tab ref={googleTabRef} disabled={institutionalProfilePending} label="Google Sign In" value="google" style={isInstitutionalLoginEnabled() ? { minWidth: 0 } : undefined} />
            <Tab ref={phoneTabRef} disabled={institutionalProfilePending} label="Phone Sign In" value="phone" style={isInstitutionalLoginEnabled() ? { minWidth: 0 } : undefined} />
            {isInstitutionalLoginEnabled() && (
              <Tab
                label={isEmailCodeAnyAddressEnabled() ? 'Email Code' : 'Institutional Email'}
                value="institutional"
                data-testid="institutional-tab"
                style={{ minWidth: 0 }}
              />
            )}
          </Tabs>

          {/* Inside an in-app browser the Google tab offers the phone number
              first, then the escape to Safari/Chrome (this page, its ?next=
              and campaign keys) in place of a Google button that would fail
              there; "Try Google sign-in anyway" covers a wrong guess. Not
              while the page only passes through (showInAppEscape). Outside
              the form (Enter in its copy-by-hand field must not submit it),
              and without the email hint, which says to use Google here. Not
              a QLink: no share token and no QLink funnel event; the dataLayer
              event keeps its four fields. An app's own menu "Open in
              browser" works here (offerAppMenu): this page's address already
              carries ?next= and the campaign keys, and no share token. */}
          {authMethod === 'google' && showInAppEscape && (
            <Box className="login-form">
              <InAppEscapePanel
                inAppBrowser={inAppBrowser}
                platform={platformLabel}
                lead={inAppEscapeLead(inAppBrowser)}
                buildTargetUrl={buildLoginEscapeUrl}
                onUsePhone={usePhoneFromEscape}
                offerAppMenu
                onTryGoogleAnyway={onGoogleSignUp}
              />
            </Box>
          )}

          {authMethod === 'google' && !showInAppEscape && (
            <form noValidate className="login-form" onSubmit={handleSubmit(signIn)}>
              <Button
                variant="outlined"
                color="secondary"
                className="google-btn"
                id="google_login_btn"
                onClick={onGoogleSignUp}
              >
                <img alt="google" src={googleLogo} width='30' height='30' className="logo" ></img>
                Sign In with Google
              </Button>
              {/* E1: email/password registration was retired (/register now
                  redirects here). Reassure people who originally signed up with
                  email — Google sign-in with the same address adopts their
                  existing account (QLearn-service UserManager.registerOAuth2User
                  matches on email and keeps account + history). */}
              <Box component="p" className="email-user-hint" data-testid="email-user-hint">
                Previously signed up with email? Use Google sign-in with the same
                address — your account and history carry over.
              </Box>
            </form>
          )}

          {/* The effect on userDetailsFromTokenRes above is the only navigation
              after a phone sign-in; see PhoneAuthSection's navigateOnSignIn.
              onUseGoogle: its code screen offers the Google tab after 30 s
              to a number with no account yet. */}
          {authMethod === 'phone' && (
            <PhoneAuthSection
              navigateOnSignIn={false}
              onUseGoogle={switchToGoogleFromPhone}
              useGoogleLabel={phoneGoogleLabel}
            />
          )}

          {authMethod === 'institutional' && isInstitutionalLoginEnabled() && (
            <InstitutionalSignIn
              key={institutionalAttempt}
              onSignedIn={finishInstitutionalSession}
              onProfilePending={setInstitutionalProfilePending}
              onUseGoogle={onGoogleSignUp}
              onUsePhone={() => setAuthMethod('phone')}
            />
          )}
          </>
          )}

          {/* Each link carries its document's own title: /terms is "Terms of
              Service". ("Terms of Use" is the agreement shown in the modal
              after sign-up, a different document.) */}
          <Box component="p" className="auth-consent">
            By signing in you agree to the QLearn{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{" "}
            and{" "}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            {/* Prior notice of session recording in the agreement itself
                (review of #896): text and a link to policy 2.4, no control and
                no panel (owner request, 2026-10-28; the choice lives in
                Settings > "Help improve QLearn"). Only where recording is built
                in: never in the iPhone app. */}
            {isReplayFeatureAvailable() && (
              <>, including{" "}
                <a href="/privacy-policy#session-recording" target="_blank" rel="noopener noreferrer">session recording</a>
              </>
            )}.
          </Box>

        </Box>

        {/* Crawlable link out to the indexable marketing surface (LEG-1 §5.S3):
            the bare root renders this login view for anonymous visitors, so a
            real anchor here gives crawlers a path to medlearnity.com/qlearn/. */}
        <Box component="footer" className="login-marketing-footer">
          <a href="https://medlearnity.com/qlearn/">
            Learn more about QLearn by Medlearnity
          </a>
        </Box>
      </Box></>

  );
};

export default LoginView;
